/**
 * The "Tasks" list under the timer — the tasks the user is working on this
 * session. Membership is edited through AddTasksDialog; each row resolves its
 * task via `useTask(id)` so it stays fresh with edits made in the drawer.
 */

import { useEffect, useState } from 'react';
import { DateChip, Icon, PriorityDot } from '@k-os/ui';
import { ApiError } from '../../api/client';
import { useTask, useToggleTaskDone } from '../../api/tasks';
import { usePreviewStore } from '../../lib/preview-store';
import { usePomodoroStore } from '../../lib/pomodoro-store';
import { toRowModel } from '../../screens/_task-row';
import { AddTasksDialog } from './AddTasksDialog';
import styles from './PomodoroTaskList.module.css';

function TaskItem({ id }: { id: string }) {
  const { data, error } = useTask(id);
  const toggle = useToggleTaskDone();
  const open = usePreviewStore((s) => s.open);
  const focusTaskId = usePomodoroStore((s) => s.focusTaskId);
  const setFocus = usePomodoroStore((s) => s.setFocus);
  const removeTask = usePomodoroStore((s) => s.removeTask);

  const notFound = error instanceof ApiError && error.status === 404;
  useEffect(() => {
    if (notFound) removeTask(id);
  }, [notFound, id, removeTask]);

  if (!data) {
    return <li className={`${styles.row} ${styles.skeleton}`} aria-hidden />;
  }

  const task = data.task;
  const row = toRowModel(task);
  const done = task.status === 'done';
  const focused = focusTaskId === id;

  return (
    <li className={`${styles.row} ${focused ? styles.focused : ''}`}>
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-label={done ? 'Mark as not done' : 'Mark as done'}
        className={`${styles.check} ${done ? styles.checkOn : ''}`}
        onClick={() => toggle.mutate({ id: task.id, status: task.status })}
      >
        {done && <Icon name="check" size={12} />}
      </button>

      <button type="button" className={styles.title} onClick={() => open(task.id)}>
        <span className={done ? styles.done : undefined}>{task.title}</span>
      </button>

      <span className={styles.meta}>
        {row.dateLabel && <DateChip label={row.dateLabel} state={row.dateState ?? 'normal'} />}
        <PriorityDot priority={task.priority} />
      </span>

      <button
        type="button"
        className={`${styles.focusBtn} ${focused ? styles.focusOn : ''}`}
        aria-pressed={focused}
        aria-label={focused ? 'Stop focusing on this task' : 'Focus on this task'}
        title={focused ? 'Current focus' : 'Focus on this'}
        onClick={() => setFocus(focused ? null : id)}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3" fill={focused ? 'currentColor' : 'none'} />
        </svg>
      </button>
    </li>
  );
}

export function PomodoroTaskList() {
  const taskIds = usePomodoroStore((s) => s.taskIds);
  const [adding, setAdding] = useState(false);

  return (
    <section className={styles.list} aria-label="Tasks">
      <header className={styles.head}>
        <h2 className={styles.heading}>Tasks</h2>
        <button type="button" className={styles.add} onClick={() => setAdding(true)}>
          <Icon name="plus" size={13} />
          Add tasks
        </button>
      </header>

      {taskIds.length === 0 ? (
        <p className={styles.empty}>No tasks yet — add what you&apos;ll work on.</p>
      ) : (
        <ul className={styles.rows}>
          {taskIds.map((id) => (
            <TaskItem key={id} id={id} />
          ))}
        </ul>
      )}

      <AddTasksDialog open={adding} onOpenChange={setAdding} />
    </section>
  );
}
